import {React, useState, useEffect} from "react";
import {useAuth} from "../../AuthContext/AuthContext";
import Typography from "@mui/material/Typography";
import {Button} from "@mui/material";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {useSnackbar} from "notistack";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import Slider from "@mui/material/Slider";
import Files from "react-files";
import {Helper} from "../../tools/Helper";
import {api_Routes} from "../../api_Routes";
import {useNavigate, useLocation, useParams} from "react-router-dom";
import MainLoader from "../common/main-loader";
import {useCenteredSnackbar} from "../../tools/useCenteredSnackbar";

const ServicesEdit = () => {
    const {auth} = useAuth();
    const {id} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {enqueueSnackbar,closeSnackbar} = useSnackbar();
    const { showCenteredPopup } = useCenteredSnackbar();

    const action = key => (
        <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => closeSnackbar(key)}
        >
            <CloseIcon fontSize="small" />
        </IconButton>
    );
    const [selectedCategoryDetails, setSelectedCategoryDetails] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState("");
    const [selectedSubcategory, setSelectedSubcategory] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isGetDataLoading, setIsGetDataLoading] = useState(true);
    const [panelExist, setPanelExist] = useState(true);
    const [responseMessage, setResponseMessage] = useState('');
    const [selectedFont, setSelectedFont] = useState("Mockads");
    const [selectedColor, setSelectedColor] = useState("#EBF2FF");
    const [logoName, setLogoName] = useState("LOREM");
    const [fontSize, setFontSize] = useState(44);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [serviceData, setServiceData] = useState(null);
    const [serviceType, setServiceType] = useState('');
    const [specificFormData, setSpecificFormData] = useState({});
    const [bannerLogo, setBannerLogo] = useState(null);
    const [bannerCustomLogo, setBannerCustomLogo] = useState(null);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        customLogo: "",
        logo: null,
    });
    const [uploadOption, setUploadOption] = useState("logo");

    const fonts = ["Mockads", "Escopedia", "Great Vibes"];
    const colors = ["#FFFFFF", "#303030", "#ECA601"];

    const handleUploadOptionChange = (option) => {
        setUploadOption(option);
        if (option === "customLogo") {
            setFormData((prev) => ({...prev, customLogo: ""}));
        } else {
            setFormData((prev) => ({...prev, logo: null}));
        }
    };

    const handleFileChange = (files) => {
        if (files && files.length > 0) {
            setFormData((prev) => ({...prev, logo: files[0]}));
        }
    };

    const handleBannerFileChange = (files) => {
        if (files && files.length > 0) {
            setBannerLogo(files[0]);
        }
    };

    const handleFontChange = (font) => {
        setSelectedFont(font);
    };

    const handleColorClick = (color) => {
        setSelectedColor(color);
    };

    const handleFontSizeChange = (event, newValue) => {
        setFontSize(newValue);
    };

    const toggleUploadOption = () => {
        if (uploadOption === "logo") {
            handleUploadOptionChange("customLogo");
        } else {
            handleUploadOptionChange("logo");
        }
    };

    const fetchServiceData = async () => {
        setIsGetDataLoading(true);
        const { response, message } = await Helper.Get({
            url: `${api_Routes.OurServices.getByUuid}${id}`,
            hasToken: true,
        });

        if (response) {
            const data = response.data;
            setServiceData(data);

            // Set form data based on the fetched service
            setFormData({
                title: data.title || "",
                description: data.description || "",
                customLogo: data.customLogo || "",
                logo: data.logo || null,
            });

            // Set category and subcategory
            setSelectedCategories(data.our_service);
            setSelectedSubcategory(data.our_service_type);

            // Set groups if any
            if (data.groups && data.groups.length > 0) {
                setSelectedGroups(data.groups);
            }

            // Determine service type based on available relationships
            if (data.pin) {
                setServiceType('pin');
                setSpecificFormData(data.pin);
            } else if (data.banner) {
                setServiceType('banner');
                setSpecificFormData(data.banner);

                // Handle banner logo and custom logo
                if (data.banner.logo) {
                    setBannerLogo(data.banner.logo);
                }

                if (data.banner.CustomLogo) {
                    setBannerCustomLogo(data.banner.CustomLogo);
                    setUploadOption("customLogo");
                    setSelectedFont(data.banner.CustomLogo.fontFamily || "Mockads");
                    setSelectedColor(data.banner.CustomLogo.color || "#EBF2FF");
                    setLogoName(data.banner.CustomLogo.logoName || "LOREM");
                    setFontSize(parseInt(data.banner.CustomLogo.fontSize) || 44);
                }
            } else if (data.TopResult) {
                setServiceType('topresult');
                setSpecificFormData(data.TopResult);
            } else if (data.tickets_ads) {
                setServiceType('tickets_ads');
                setSpecificFormData(data.tickets_ads);
            } else if (data.trust) {
                setServiceType('trust');
                setSpecificFormData(data.trust);
            } else if (data.status === 'pending' || data.status === 'new_request') {
                // For pending/new_request services, set form data directly
                setFormData(prev => ({
                    ...prev,
                    title: data.title || "",
                    description: data.description || "",
                    customLogo: data.customLogo || "",
                    logo: data.logo || null,
                }));

                if (data.logo) {
                    setBannerLogo(data.logo);
                } else if (data.customLogo) {
                    setBannerCustomLogo(data.customLogo);
                    setUploadOption("customLogo");
                    setSelectedFont(data.customLogo.fontFamily || "Mockads");
                    setSelectedColor(data.customLogo.color || "#EBF2FF");
                    setLogoName(data.customLogo.logoName || "LOREM");
                    setFontSize(parseInt(data.customLogo.fontSize) || 44);
                }
                setSpecificFormData(data);
            }

            setIsGetDataLoading(false);
        } else {
            showCenteredPopup(message || "Failed to fetch service data",
                { variant: "error",
                    persist: true });
            // enqueueSnackbar(message || "Failed to fetch service data", { variant: "error",
            //     persist: true,
            //     action, });
            setIsGetDataLoading(false);
        }
    };

    const handleButtonClick = async () => {
        if (!auth) {
            showCenteredPopup("Please login first",
                { variant: "warning",
                    persist: true });
            // enqueueSnackbar("Please login first", { variant: "warning",
            //     persist: true,
            //     action, });
            localStorage.setItem("formData", JSON.stringify(formData));
            localStorage.setItem(
                "customLogo",
                JSON.stringify({
                    fontSize: fontSize,
                    color: selectedColor,
                    fontFamily: selectedFont,
                    logoName,
                })
            );
            navigate("/login", { state: { from: location } });
        } else {
            await handleSubmit();
        }
    };

    const handleAddPanelClick = async () => {
        navigate("/AddPanel", {state: {from: location}});
    };

    const handleSpecificFormChange = (key, value) => {
        setSpecificFormData(prev => ({...prev, [key]: value}));
    };

    const updateService = async () => {
        setIsLoading(true);

        let form_data = new FormData();

        // Add basic service fields
        if (formData.title) form_data.append("title", formData.title);
        if (formData.description) form_data.append("description", formData.description);

        // Handle logo upload based on service type
        if (serviceType === 'banner' || serviceData?.our_service_id === 9) {
            // For banner services
            if (uploadOption === "logo" && bannerLogo) {
                if (bannerLogo instanceof File) {
                    form_data.append("logo", bannerLogo);
                }
                // If it's a string (existing logo), the server will handle it
            } else if (uploadOption === "customLogo") {
                form_data.append(
                    "customLogo",
                    JSON.stringify({
                        fontSize: `${fontSize}px`,
                        color: selectedColor,
                        fontFamily: selectedFont,
                        logoName,
                    })
                );
            }
        } else {
            // For all other service types
            if (uploadOption === "logo" && formData.logo) {
                if (formData.logo instanceof File) {
                    form_data.append("logo", formData.logo);
                }
                // If it's a string (existing logo), the server will handle it
            } else if (uploadOption === "customLogo") {
                form_data.append(
                    "customLogo",
                    JSON.stringify({
                        fontSize: `${fontSize}px`,
                        color: selectedColor,
                        fontFamily: selectedFont,
                        logoName,
                    })
                );
            }
        }

        const { response, message } = await Helper.Post({
            url: `${api_Routes.OurServices.update}${id}`,
            data: form_data,
            hasToken: true,
        });

        if (response) {
            showCenteredPopup("Service updated successfully",
                { variant: "success",
                    persist: true });
            // enqueueSnackbar("Service updated successfully", { variant: "success",
            //     persist: true,
            //     action, });
            navigate(`/dashboard/order-history`);
        } else {
            showCenteredPopup(message || "Failed to update service",
                { variant: "error",
                    persist: true });
            // enqueueSnackbar(message || "Failed to update service", { variant: "error",
            //     persist: true,
            //     action, });
            if (message === 'This Panel Not Yours ,Please add This Panel To Your Account' || message === 'Panel does not exist, Please Add this Panel First') {
                setPanelExist(false);
                setResponseMessage(message);
            }
        }

        setIsLoading(false);
    };

    const handleSubmit = async () => {
        let missingFields = [];

        if (!selectedSubcategory && selectedCategoryDetails?.with_type) {
            missingFields.push("service type");
        }

        if (selectedCategoryDetails?.FiledTitle && !formData.title) {
            missingFields.push("title");
        }

        if (selectedCategoryDetails?.FiledDescription && !formData.description) {
            missingFields.push("description");
        }

        if (selectedCategoryDetails?.FiledImage) {
            if (uploadOption === "logo") {
                if ((serviceType === 'banner' || serviceData?.our_service_id === 9) && !bannerLogo) {
                    missingFields.push("banner logo");
                } else if (serviceType !== 'banner' && serviceData?.our_service_id !== 9 && !formData.logo) {
                    missingFields.push("logo");
                }
            } else if (
                uploadOption === "customLogo" &&
                (!logoName || !selectedColor || !selectedFont || !fontSize)
            ) {
                missingFields.push("customLogo properties");
            }
        }

        if (missingFields.length > 0) {
            showCenteredPopup( `Please fill in all required fields: ${missingFields.join(", ")}`,
                { variant: "error",
                    persist: true });
            // enqueueSnackbar(
            //     `Please fill in all required fields: ${missingFields.join(", ")}`,
            //     {
            //         variant: "error",
            //         persist: true,
            //         action,
            //         anchorOrigin: { vertical: "top", horizontal: "right" },
            //     }
            // );
            return;
        }

        if (!id) return;
        await updateService();
    };

    const renderServiceSpecificFields = () => {
        if (serviceType) {
            switch (serviceType) {
                case 'pin':
                    return (
                        <Box sx={{mt: 3, p: 2, border: '1px solid #ddd', borderRadius: '8px'}}>
                            <Typography variant="h6" sx={{mb: 2}}>Pin Details</Typography>
                            <TextField
                                label="Pin Title"
                                value={formData.title || ''}
                                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Pin Description"
                                value={formData.description || ''}
                                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />
                        </Box>
                    );
                case 'banner':
                    return (
                        <Box sx={{mt: 3, p: 2, border: '1px solid #ddd', borderRadius: '8px'}}>
                            <Typography variant="h6" sx={{mb: 2}}>Banner Details</Typography>
                            <TextField
                                label="Banner Title"
                                value={formData.title || ''}
                                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Banner Description"
                                value={formData.description || ''}
                                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />

                            {/* Banner Logo Section */}
                            <Box sx={{mt: 3}}>
                                <Typography
                                    sx={{
                                        marginBottom: "10px",
                                        fontWeight: "600",
                                        fontSize: "24px",
                                        lineHeight: "30px",
                                        color: "#474747",
                                    }}
                                >
                                    Banner Logo *
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: "20px 0",
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={toggleUploadOption}
                                        sx={{
                                            fontWeight: "600",
                                            border: "1px solid #3F78E0",
                                            textTransform: "capitalize",
                                            textAlign: "center",
                                            borderRadius: "10px",
                                            padding: "3px 40px",
                                            background: "#FFFFFF",
                                            color: "#3F78E0",
                                            boxShadow: "0px 5px 5px #00000040, inset 4px 7px 5px #FFFFFF30",
                                            "&:hover": {
                                                background: "none",
                                            },
                                        }}
                                    >
                                        {uploadOption === "logo"
                                            ? "Switch to Custom Logo"
                                            : "Switch to Photo Upload"}
                                    </Button>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: {xs: "column", md: "row"},
                                        position: "relative",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        gap: "20px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: {xs: "100%", md: "45%"},
                                            opacity: uploadOption === "customLogo" ? 0.5 : 1,
                                            pointerEvents: uploadOption === "customLogo" ? "none" : "auto",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: {xs: "300px", md: "100%", lg: "300px"},
                                                textAlign: "center",
                                                backgroundColor: "#FFFFFF",
                                                cursor: uploadOption === "customLogo" ? "not-allowed" : "pointer",
                                                position: "relative",
                                                border: "#303030 solid 1px",
                                                borderRadius: "15px",
                                            }}
                                        >
                                            <Files
                                                onChange={handleBannerFileChange}
                                                onError={(error) => console.error("File upload error:", error)}
                                                accepts={["image/*"]}
                                                clickable
                                                multiple={false}
                                                maxFileSize={10485760}
                                                disabled={uploadOption === "customLogo"}
                                                style={{
                                                    opacity: 0,
                                                    position: "absolute",
                                                    width: "100%",
                                                    height: "100%",
                                                    top: 0,
                                                    left: 0,
                                                }}
                                            />
                                            {bannerLogo ? (
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        width: "80%",
                                                        height: "80%",
                                                        borderRadius: "10px",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <img
                                                        src={typeof bannerLogo === 'string' ? bannerLogo : URL.createObjectURL(bannerLogo)}
                                                        alt="Banner Logo"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "contain",
                                                            borderRadius: "10px",
                                                        }}
                                                    />
                                                    <IconButton
                                                        onClick={() => {
                                                            setBannerLogo(null);
                                                        }}
                                                        sx={{
                                                            position: "absolute",
                                                            top: "5px",
                                                            right: "5px",
                                                            color: "white",
                                                            backgroundColor: "red",
                                                            borderRadius: "10px",
                                                            "&:hover": {
                                                                backgroundColor: "red",
                                                            },
                                                        }}
                                                    >
                                                        <CloseIcon/>
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    Click to upload a banner logo
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: {xs: "100%", md: "50%"},
                                            opacity: uploadOption === "logo" ? 0.5 : 1,
                                            pointerEvents: uploadOption === "logo" ? "none" : "auto",
                                            background: "#FFFFFF",
                                            paddingBottom: "25px",
                                            border: "1px solid #303030",
                                            borderRadius: "10px",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: {
                                                    xs: "column",
                                                    sm: "row",
                                                    md: "column",
                                                    lg: "row",
                                                },
                                                justifyContent: {
                                                    xs: "space-between",
                                                    sm: "space-around",
                                                    md: "space-between",
                                                },
                                                alignItems: "center",
                                                height: "100%",
                                                padding: {
                                                    xs: "20px 20px",
                                                    sm: "0 20px",
                                                    md: "20px 20px",
                                                    lg: "0 20px",
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "10px",
                                                    width: "90%",
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            color: "#474747",
                                                            fontSize: "20px",
                                                            fontWeight: "600",
                                                            lineHeight: "30px",
                                                        }}
                                                    >
                                                        Pick a Custom Font
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        value={logoName}
                                                        onChange={(e) => setLogoName(e.target.value)}
                                                        placeholder="Type Your Logo Name here"
                                                        size="small"
                                                        InputLabelProps={{
                                                            style: {
                                                                fontSize: "16px",
                                                                color: "#5F5F5F",
                                                                lineHeight: "24px",
                                                                fontWeight: "600",
                                                                letterSpacing: "1px",
                                                            },
                                                        }}
                                                        sx={{
                                                            width: "95%",
                                                            boxShadow: "-2px 2px 4px #00000040",
                                                            borderRadius: "10px",
                                                            backgroundColor: "white",
                                                            marginTop: "10px",
                                                        }}
                                                    />
                                                </Box>

                                                <Box>
                                                    <Typography>Choose a Font</Typography>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            gap: "10px",
                                                            marginTop: "4px",
                                                            justifyContent: "space-evenly",
                                                        }}
                                                    >
                                                        {fonts.map((font) => (
                                                            <Box
                                                                key={font}
                                                                sx={{
                                                                    background: "#E5EDFF",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    flexDirection: "column",
                                                                    gap: "4px",
                                                                    border: "1px solid #959595",
                                                                    borderRadius: "10px",
                                                                    padding: {
                                                                        xs: "4px 6px",
                                                                        sm: "4px 12px",
                                                                        md: "4px 25px",
                                                                    },
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: font,
                                                                        fontSize: "20px",
                                                                        lineHeight: "24px",
                                                                        fontWeight: "600",
                                                                        color: "#474747",
                                                                    }}
                                                                >
                                                                    lorem
                                                                </Typography>
                                                                <Radio
                                                                    checked={selectedFont === font}
                                                                    onChange={() => handleFontChange(font)}
                                                                    sx={{
                                                                        color: "primary.main",
                                                                    }}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                                <Typography>Choose a color and font size</Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "6px",
                                                        }}
                                                    >
                                                        <Box sx={{display: "flex", gap: 2}}>
                                                            {colors.map((color) => (
                                                                <Box
                                                                    key={color}
                                                                    onClick={() => handleColorClick(color)}
                                                                    sx={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        borderRadius: "50%",
                                                                        backgroundColor: color,
                                                                        cursor: "pointer",
                                                                        border: "1px #303030 solid",
                                                                        transition: "border 0.3s ease",
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "6px",
                                                        }}
                                                    >
                                                        <Slider
                                                            value={fontSize}
                                                            onChange={handleFontSizeChange}
                                                            min={20}
                                                            max={50}
                                                            step={1}
                                                            valueLabelDisplay="auto"
                                                            sx={{width: "100px"}}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    height: "200px",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "18px",
                                                        lineHeight: "28px",
                                                        color: "#474747",
                                                    }}
                                                >
                                                    Your Logo demo
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        background: "radial-gradient(circle, #51AAFD, #002D68)",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        position: "relative",
                                                        padding: "40px",
                                                        borderRadius: "15px",
                                                        width: "100px",
                                                        height: "80px",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: `${fontSize}px`,
                                                            fontWeight: "700",
                                                            color: selectedColor,
                                                            fontFamily: selectedFont,
                                                        }}
                                                    >
                                                        {logoName || "LOREM"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    );
                case 'tickets_ads':
                    return (
                        <Box sx={{mt: 3, p: 2, border: '1px solid #ddd', borderRadius: '8px'}}>
                            <Typography variant="h6" sx={{mb: 2}}>Tickets Ads Details</Typography>
                            {/*{*/}
                            {/*  selectedCategories.FiledTitle === 1 &&*/}
                            {/*  <TextField*/}
                            {/*      label="Title"*/}
                            {/*      value={specificFormData.title || ''}*/}
                            {/*      onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}/}
                            {/*      fullWidth*/}
                            {/*      margin="normal"*/}
                            {/*  />*/}
                            {/*}*/}
                            <TextField
                                label="Description"
                                value={formData.description || ''}
                                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />
                        </Box>
                    );
                default:
                    return null;
            }
        } else {
            switch (serviceData.our_service_id) {
                case 10:
                    return (
                        <Box sx={{mt: 3, p: 2, border: '1px solid #ddd', borderRadius: '8px'}}>
                            <Typography variant="h6" sx={{mb: 2}}>Pin Details</Typography>
                            <TextField
                                label="Pin Title"
                                value={formData.title || ''}  // Use formData instead of specificFormData
                                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Pin Description"
                                value={formData.description || ''}  // Use formData instead of specificFormData
                                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />
                        </Box>
                    );
                case 9:
                    return (
                        <Box sx={{mt: 3, p: 2, border: '1px solid #ddd', borderRadius: '8px'}}>
                            <Typography variant="h6" sx={{mb: 2}}>Banner Details</Typography>
                            <TextField
                                label="Banner Title"
                                value={formData.title || ''}  // Use formData instead of specificFormData
                                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Banner Description"
                                value={formData.description || ''}  // Use formData instead of specificFormData
                                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                                fullWidth
                                margin="normal"
                            />

                            {/* Banner Logo Section */}
                            <Box sx={{mt: 3}}>
                                <Typography
                                    sx={{
                                        marginBottom: "10px",
                                        fontWeight: "600",
                                        fontSize: "24px",
                                        lineHeight: "30px",
                                        color: "#474747",
                                    }}
                                >
                                    Banner Logo *
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: "20px 0",
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={toggleUploadOption}
                                        sx={{
                                            fontWeight: "600",
                                            border: "1px solid #3F78E0",
                                            textTransform: "capitalize",
                                            textAlign: "center",
                                            borderRadius: "10px",
                                            padding: "3px 40px",
                                            background: "#FFFFFF",
                                            color: "#3F78E0",
                                            boxShadow: "0px 5px 5px #00000040, inset 4px 7px 5px #FFFFFF30",
                                            "&:hover": {
                                                background: "none",
                                            },
                                        }}
                                    >
                                        {uploadOption === "logo"
                                            ? "Switch to Custom Logo"
                                            : "Switch to Photo Upload"}
                                    </Button>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: {xs: "column", md: "row"},
                                        position: "relative",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        gap: "20px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: {xs: "100%", md: "45%"},
                                            opacity: uploadOption === "customLogo" ? 0.5 : 1,
                                            pointerEvents: uploadOption === "customLogo" ? "none" : "auto",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: {xs: "300px", md: "100%", lg: "300px"},
                                                textAlign: "center",
                                                backgroundColor: "#FFFFFF",
                                                cursor: uploadOption === "customLogo" ? "not-allowed" : "pointer",
                                                position: "relative",
                                                border: "#303030 solid 1px",
                                                borderRadius: "15px",
                                            }}
                                        >
                                            <Files
                                                onChange={handleBannerFileChange}
                                                onError={(error) => console.error("File upload error:", error)}
                                                accepts={["image/*"]}
                                                clickable
                                                multiple={false}
                                                maxFileSize={10485760}
                                                disabled={uploadOption === "customLogo"}
                                                style={{
                                                    opacity: 0,
                                                    position: "absolute",
                                                    width: "100%",
                                                    height: "100%",
                                                    top: 0,
                                                    left: 0,
                                                }}
                                            />
                                            {bannerLogo ? (
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        width: "80%",
                                                        height: "80%",
                                                        borderRadius: "10px",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <img
                                                        src={typeof bannerLogo === 'string' ? bannerLogo : URL.createObjectURL(bannerLogo)}
                                                        alt="Banner Logo"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "contain",
                                                            borderRadius: "10px",
                                                        }}
                                                    />
                                                    <IconButton
                                                        onClick={() => {
                                                            setBannerLogo(null);
                                                        }}
                                                        sx={{
                                                            position: "absolute",
                                                            top: "5px",
                                                            right: "5px",
                                                            color: "white",
                                                            backgroundColor: "red",
                                                            borderRadius: "10px",
                                                            "&:hover": {
                                                                backgroundColor: "red",
                                                            },
                                                        }}
                                                    >
                                                        <CloseIcon/>
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    Click to upload a banner logo
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: {xs: "100%", md: "50%"},
                                            opacity: uploadOption === "logo" ? 0.5 : 1,
                                            pointerEvents: uploadOption === "logo" ? "none" : "auto",
                                            background: "#FFFFFF",
                                            paddingBottom: "25px",
                                            border: "1px solid #303030",
                                            borderRadius: "10px",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: {
                                                    xs: "column",
                                                    sm: "row",
                                                    md: "column",
                                                    lg: "row",
                                                },
                                                justifyContent: {
                                                    xs: "space-between",
                                                    sm: "space-around",
                                                    md: "space-between",
                                                },
                                                alignItems: "center",
                                                height: "100%",
                                                padding: {
                                                    xs: "20px 20px",
                                                    sm: "0 20px",
                                                    md: "20px 20px",
                                                    lg: "0 20px",
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "10px",
                                                    width: "90%",
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            color: "#474747",
                                                            fontSize: "20px",
                                                            fontWeight: "600",
                                                            lineHeight: "30px",
                                                        }}
                                                    >
                                                        Pick a Custom Font
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        value={logoName}
                                                        onChange={(e) => setLogoName(e.target.value)}
                                                        placeholder="Type Your Logo Name here"
                                                        size="small"
                                                        InputLabelProps={{
                                                            style: {
                                                                fontSize: "16px",
                                                                color: "#5F5F5F",
                                                                lineHeight: "24px",
                                                                fontWeight: "600",
                                                                letterSpacing: "1px",
                                                            },
                                                        }}
                                                        sx={{
                                                            width: "95%",
                                                            boxShadow: "-2px 2px 4px #00000040",
                                                            borderRadius: "10px",
                                                            backgroundColor: "white",
                                                            marginTop: "10px",
                                                        }}
                                                    />
                                                </Box>

                                                <Box>
                                                    <Typography>Choose a Font</Typography>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            gap: "10px",
                                                            marginTop: "4px",
                                                            justifyContent: "space-evenly",
                                                        }}
                                                    >
                                                        {fonts.map((font) => (
                                                            <Box
                                                                key={font}
                                                                sx={{
                                                                    background: "#E5EDFF",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    flexDirection: "column",
                                                                    gap: "4px",
                                                                    border: "1px solid #959595",
                                                                    borderRadius: "10px",
                                                                    padding: {
                                                                        xs: "4px 6px",
                                                                        sm: "4px 12px",
                                                                        md: "4px 25px",
                                                                    },
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: font,
                                                                        fontSize: "20px",
                                                                        lineHeight: "24px",
                                                                        fontWeight: "600",
                                                                        color: "#474747",
                                                                    }}
                                                                >
                                                                    lorem
                                                                </Typography>
                                                                <Radio
                                                                    checked={selectedFont === font}
                                                                    onChange={() => handleFontChange(font)}
                                                                    sx={{
                                                                        color: "primary.main",
                                                                    }}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                                <Typography>Choose a color and font size</Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "6px",
                                                        }}
                                                    >
                                                        <Box sx={{display: "flex", gap: 2}}>
                                                            {colors.map((color) => (
                                                                <Box
                                                                    key={color}
                                                                    onClick={() => handleColorClick(color)}
                                                                    sx={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        borderRadius: "50%",
                                                                        backgroundColor: color,
                                                                        cursor: "pointer",
                                                                        border: "1px #303030 solid",
                                                                        transition: "border 0.3s ease",
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "6px",
                                                        }}
                                                    >
                                                        <Slider
                                                            value={fontSize}
                                                            onChange={handleFontSizeChange}
                                                            min={20}
                                                            max={50}
                                                            step={1}
                                                            valueLabelDisplay="auto"
                                                            sx={{width: "100px"}}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    height: "200px",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "18px",
                                                        lineHeight: "28px",
                                                        color: "#474747",
                                                    }}
                                                >
                                                    Your Logo demo
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        background: "radial-gradient(circle, #51AAFD, #002D68)",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        position: "relative",
                                                        padding: "40px",
                                                        borderRadius: "15px",
                                                        width: "100px",
                                                        height: "80px",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: `${fontSize}px`,
                                                            fontWeight: "700",
                                                            color: selectedColor,
                                                            fontFamily: selectedFont,
                                                        }}
                                                    >
                                                        {logoName || "LOREM"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    );
                case 'tickets_ads':
                    return (
                        <Box sx={{mt: 3, p: 2, border: '1px solid #ddd', borderRadius: '8px'}}>
                            <Typography variant="h6" sx={{mb: 2}}>Tickets Ads Details</Typography>
                            {/*{*/}
                            {/*  selectedCategories.FiledTitle === 1 &&*/}
                            {/*  <TextField*/}
                            {/*      label="Title"*/}
                            {/*      value={specificFormData.title || ''}*/}
                            {/*      onChange={(e) => handleSpecificFormChange('title', e.target.value)}*/}
                            {/*      fullWidth*/}
                            {/*      margin="normal"*/}
                            {/*  />*/}
                            {/*}*/}
                            <TextField
                                label="Description"
                                value={specificFormData.subject || ''}
                                onChange={(e) => handleSpecificFormChange('subject', e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                        </Box>
                    );
                default:
                    return null;
            }
        }

    };

    const renderLogoSection = () => {
        if (!selectedCategoryDetails?.FiledImage) return null;

        return (
            <Box sx={{width: {xs: "100%", sm: "100%"}, mt: 3}}>
                <Typography
                    sx={{
                        marginBottom: "10px",
                        fontWeight: "600",
                        fontSize: "24px",
                        lineHeight: "30px",
                        color: "#474747",
                    }}
                >
                    Logo Image *
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "20px 0",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={toggleUploadOption}
                        sx={{
                            fontWeight: "600",
                            border: "1px solid #3F78E0",
                            textTransform: "capitalize",
                            textAlign: "center",
                            borderRadius: "10px",
                            padding: "3px 40px",
                            background: "#FFFFFF",
                            color: "##3F78E0",
                            boxShadow: "0px 5px 5px #00000040, inset 4px 7px 5px #FFFFFF30",
                            "&:hover": {
                                background: "none",
                            },
                        }}
                    >
                        {uploadOption === "logo"
                            ? "Switch to Custom Logo"
                            : "Switch to Photo Upload"}
                    </Button>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: {xs: "column", md: "row"},
                        position: "relative",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: "20px",
                    }}
                >
                    <Box
                        sx={{
                            width: {xs: "100%", md: "45%"},
                            opacity: uploadOption === "customLogo" ? 0.5 : 1,
                            pointerEvents: uploadOption === "customLogo" ? "none" : "auto",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: {xs: "300px", md: "100%", lg: "300px"},
                                textAlign: "center",
                                backgroundColor: "#FFFFFF",
                                cursor: uploadOption === "customLogo" ? "not-allowed" : "pointer",
                                position: "relative",
                                border: "#303030 solid 1px",
                                borderRadius: "15px",
                            }}
                        >
                            <Files
                                onChange={serviceType === 'banner' ? handleBannerFileChange : handleFileChange}
                                onError={(error) => console.error("File upload error:", error)}
                                accepts={["image/*"]}
                                clickable
                                multiple={false}
                                maxFileSize={10485760}
                                disabled={uploadOption === "customLogo"}
                                style={{
                                    opacity: 0,
                                    position: "absolute",
                                    width: "100%",
                                    height: "100%",
                                    top: 0,
                                    left: 0,
                                }}
                            />
                            {(serviceType === 'banner' ? bannerLogo : formData.logo) ? (
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "80%",
                                        height: "80%",
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                    }}
                                >
                                    <img
                                        src={
                                            serviceType === 'banner'
                                                ? (typeof bannerLogo === 'string' ? bannerLogo : URL.createObjectURL(bannerLogo))
                                                : (typeof formData.logo === 'string' ? formData.logo : URL.createObjectURL(formData.logo))
                                        }
                                        alt="Uploaded Logo"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                            borderRadius: "10px",
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => {
                                            if (serviceType === 'banner') {
                                                setBannerLogo(null);
                                            } else {
                                                setFormData((prev) => ({...prev, logo: null}));
                                            }
                                        }}
                                        sx={{
                                            position: "absolute",
                                            top: "5px",
                                            right: "5px",
                                            color: "white",
                                            backgroundColor: "red",
                                            borderRadius: "10px",
                                            "&:hover": {
                                                backgroundColor: "red",
                                            },
                                        }}
                                    >
                                        <CloseIcon/>
                                    </IconButton>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    Click to upload a logo
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            width: {xs: "100%", md: "50%"},
                            opacity: uploadOption === "logo" ? 0.5 : 1,
                            pointerEvents: uploadOption === "logo" ? "none" : "auto",
                            background: "#FFFFFF",
                            paddingBottom: "25px",
                            border: "1px solid #303030",
                            borderRadius: "10px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: {
                                    xs: "column",
                                    sm: "row",
                                    md: "column",
                                    lg: "row",
                                },
                                justifyContent: {
                                    xs: "space-between",
                                    sm: "space-around",
                                    md: "space-between",
                                },
                                alignItems: "center",
                                height: "100%",
                                padding: {
                                    xs: "20px 20px",
                                    sm: "0 20px",
                                    md: "20px 20px",
                                    lg: "0 20px",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    width: "90%",
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            color: "#474747",
                                            fontSize: "20px",
                                            fontWeight: "600",
                                            lineHeight: "30px",
                                        }}
                                    >
                                        Pick a Custom Font
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        value={logoName}
                                        onChange={(e) => setLogoName(e.target.value)}
                                        placeholder="Type Your Logo Name here"
                                        size="small"
                                        InputLabelProps={{
                                            style: {
                                                fontSize: "16px",
                                                color: "#5F5F5F",
                                                lineHeight: "24px",
                                                fontWeight: "600",
                                                letterSpacing: "1px",
                                            },
                                        }}
                                        sx={{
                                            width: "95%",
                                            boxShadow: "-2px 2px 4px #00000040",
                                            borderRadius: "10px",
                                            backgroundColor: "white",
                                            marginTop: "10px",
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Typography>Choose a Font</Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: "10px",
                                            marginTop: "4px",
                                            justifyContent: "space-evenly",
                                        }}
                                    >
                                        {fonts.map((font) => (
                                            <Box
                                                key={font}
                                                sx={{
                                                    background: "#E5EDFF",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                    gap: "4px",
                                                    border: "1px solid #959595",
                                                    borderRadius: "10px",
                                                    padding: {
                                                        xs: "4px 6px",
                                                        sm: "4px 12px",
                                                        md: "4px 25px",
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: font,
                                                        fontSize: "20px",
                                                        lineHeight: "24px",
                                                        fontWeight: "600",
                                                        color: "#474747",
                                                    }}
                                                >
                                                    lorem
                                                </Typography>
                                                <Radio
                                                    checked={selectedFont === font}
                                                    onChange={() => handleFontChange(font)}
                                                    sx={{
                                                        color: "primary.main",
                                                    }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                                <Typography>Choose a color and font size</Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: "10px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "6px",
                                        }}
                                    >
                                        <Box sx={{display: "flex", gap: 2}}>
                                            {colors.map((color) => (
                                                <Box
                                                    key={color}
                                                    onClick={() => handleColorClick(color)}
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: "50%",
                                                        backgroundColor: color,
                                                        cursor: "pointer",
                                                        border: "1px #303030 solid",
                                                        transition: "border 0.3s ease",
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "6px",
                                        }}
                                    >
                                        <Slider
                                            value={fontSize}
                                            onChange={handleFontSizeChange}
                                            min={20}
                                            max={50}
                                            step={1}
                                            valueLabelDisplay="auto"
                                            sx={{width: "100px"}}
                                        />
                                        <Typography variant="body2">{fontSize}px</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "6px",
                                    height: "200px",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "18px",
                                        lineHeight: "28px",
                                        color: "#474747",
                                    }}
                                >
                                    Your Logo demo
                                </Typography>
                                <Box
                                    sx={{
                                        background: "radial-gradient(circle, #51AAFD, #002D68)",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        position: "relative",
                                        padding: "40px",
                                        borderRadius: "15px",
                                        width: "100px",
                                        height: "80px",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: `${fontSize}px`,
                                            fontWeight: "700",
                                            color: selectedColor,
                                            fontFamily: selectedFont,
                                        }}
                                    >
                                        {logoName || "LOREM"}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

    useEffect(() => {
        if (!id) return;
        fetchServiceData();
    }, [id]);

    if (isGetDataLoading) {
        return <MainLoader isFull={true}/>;
    }

    return (
        <Box sx={{padding: {xs: "30px", md: "30px 80px"}}}>
            <Box sx={{marginTop: "20px"}}>
                <Typography
                    sx={{
                        fontSize: "40px",
                        lineHeight: "45px",
                        fontWeight: "700",
                        color: "#474747",
                        textAlign: "center",
                    }}
                >
                    Edit Service
                </Typography>
                <Typography
                    sx={{
                        fontSize: "18px",
                        lineHeight: "20px",
                        fontWeight: "400",
                        color: "#474747",
                        textAlign: "center",
                        marginTop: "30px",
                    }}
                >
                    Edit your service details
                </Typography>
            </Box>

            {renderServiceSpecificFields()}
            {renderLogoSection()}

            <Box
                sx={{display: "flex", justifyContent: "center", marginTop: "30px"}}
            >
                {
                    panelExist ? (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                background: "#ECA601",
                                padding: "10px 50px",
                                borderRadius: "10px",
                                color: "#F1F5FD",
                                position: "relative",
                                lineHeight: "20px",
                                overflow: "hidden",
                                fontWeight: "700",
                                fontSize: "18px",
                                boxShadow: "0px 3px 3px #00000040",
                                "&:before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    borderRadius: "0 20px 20px 0",
                                    background: "rgba(255, 255, 255, 0.1)",
                                    filter: "blur(4px)",
                                    zIndex: 1,
                                },
                                "&:hover:before": {
                                    background: "rgba(255, 255, 255, 0.2)",
                                },
                            }}
                            disabled={isLoading}
                            onClick={handleButtonClick}
                        >
                            {isLoading ? (
                                    <CircularProgress size={22} sx={{color: "white"}}/>
                                ) :
                                "Update Service"
                            }
                        </Button>
                    ) : (
                        <Box sx={{textAlign: "center"}}>
                            <Typography
                                sx={{
                                    fontSize: "18px",
                                    lineHeight: "20px",
                                    fontWeight: "400",
                                    color: "#474747",
                                    textAlign: "center",
                                    margin: "30px",
                                }}
                            >
                                {responseMessage}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    background: "#ECA601",
                                    padding: "10px 50px",
                                    borderRadius: "10px",
                                    color: "#F1F5FD",
                                    position: "relative",
                                    lineHeight: "20px",
                                    overflow: "hidden",
                                    fontWeight: "700",
                                    fontSize: "18px",
                                    boxShadow: "0px 3px 3px #00000040",
                                    "&:before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        borderRadius: "0 20px 20px 0",
                                        background: "rgba(255, 255, 255, 0.1)",
                                        filter: "blur(4px)",
                                        zIndex: 1,
                                    },
                                    "&:hover:before": {
                                        background: "rgba(255, 255, 255, 0.2)",
                                    },
                                }}
                                disabled={isLoading}
                                onClick={handleAddPanelClick}
                            >
                                {isLoading ? (
                                    <CircularProgress size={22} sx={{color: "white"}}/>
                                ) : (
                                    "Add Panel"
                                )}
                            </Button>
                        </Box>
                    )
                }
            </Box>
        </Box>
    );
};

export default ServicesEdit;